import styled from 'styled-components';

import { JobBookmarkState } from '@xing-com/crate-common-graphql-types';
import { getXingJobsSlugPath } from '@xing-com/crate-core-routes';
import { ActionButtons } from '@xing-com/crate-jobs-components-job-teaser-options';
import type { SimilarJobData } from '@xing-com/crate-jobs-helpers';
import { getTimeDifferenceInDays } from '@xing-com/crate-jobs-helpers';
import { useJobBookmarkToggle } from '@xing-com/crate-jobs-hooks';
import {
  InViewTracking,
  useJobsNwtTracking,
} from '@xing-com/crate-jobs-tracking';
import {
  JobTeaserCard,
  JobTeaserListItem,
} from '@xing-com/crate-jobs-xinglets';
import { NotOnLarge, OnlyOnLarge } from '@xing-com/layout-primitives';

import { useJobBookmarkStatusBanners } from '../../hooks';

const LargeScreen = styled(OnlyOnLarge)`
  height: 100%;
`;

const CustomWrapperNotOnLarge = styled(NotOnLarge)`
  height: 100%;
`;

type SimilarJobProps = {
  ijtParam: string;
  isCarousel?: boolean;
  isInSidebar?: boolean;
  similarJob: SimilarJobData;
};

export const SimilarJob: React.FC<SimilarJobProps> = ({
  ijtParam,
  isCarousel = false,
  isInSidebar = false,
  similarJob: { job, position, trackingToken },
}) => {
  const nwtTrack = useJobsNwtTracking();
  const bookmarkState = job.userInteractions?.bookmark?.state;
  const isBookmarked =
    !!bookmarkState && bookmarkState !== JobBookmarkState.NotBookmarked;
  const { onCompleted, onError } = useJobBookmarkStatusBanners(isBookmarked);
  const isExternalJob = job.redirectsToThirdPartyUrl;

  const nwtTrackingOptions = {
    eventSchema: 'searchresult',
    itemUrn: job.globalId,
    page: 'jobs/detail',
    position,
    trackingToken,
    flags: isExternalJob ? 'external_job_ad' : undefined,
  } as const;

  const bookmarkToggle = useJobBookmarkToggle({
    jobId: job.id,
    isBookmarked,
    nwtTrackingOptions,
    onCompleted,
    onError,
  });

  const url = getXingJobsSlugPath({ slug: job.slug });
  const urlWithParams = `${url}?ijt=${ijtParam}`;

  const onClick = (): void => {
    nwtTrack({ ...nwtTrackingOptions, event: 'opened' });
  };

  const matchingHighlights =
    isInSidebar && job.matchingHighlights
      ? {
          ...job.matchingHighlights,
          matchingFacts: [],
        }
      : job.matchingHighlights;

  const jobTeaserComponentProps = {
    job,
    url: urlWithParams,
    matchingHighlights,
    isNew: !!job.refreshedAt && getTimeDifferenceInDays(job.refreshedAt) <= 2,
    onClick,
    'aria-label': job.title,
  };
  const actionButtonsProps = {
    onBookmark: bookmarkToggle,
    isBookmarked,
    noMargin: true,
    size: 'small' as const,
  };

  return (
    <InViewTracking
      nwtTrackingOptions={{ ...nwtTrackingOptions, event: 'entered_viewport' }}
    >
      <CustomWrapperNotOnLarge>
        {isCarousel ? (
          <JobTeaserCard
            {...jobTeaserComponentProps}
            headingLevel="h3"
            data-testid={`similar-job-large-screen-${job.id}`}
            actions={
              <ActionButtons {...actionButtonsProps} iconsOnly={false} />
            }
          />
        ) : (
          <JobTeaserListItem
            {...jobTeaserComponentProps}
            actions={
              <ActionButtons {...actionButtonsProps} bottom={false} iconsOnly />
            }
          />
        )}
      </CustomWrapperNotOnLarge>
      <LargeScreen>
        <JobTeaserCard
          {...jobTeaserComponentProps}
          data-testid={`similar-job-large-screen-${job.id}`}
          actions={
            <ActionButtons {...actionButtonsProps} iconsOnly={false} bottom />
          }
        />
      </LargeScreen>
    </InViewTracking>
  );
};
